import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LiveLogSettings, LiveLogSettingsDialogComponent, LiveLogSettingsDialogData } from '@portal-core/live-log/components/live-log-settings-dialog/live-log-settings-dialog.component';
import { LiveLogComponent, LiveLogSourceLinkResolver } from '@portal-core/live-log/components/live-log/live-log.component';

/**
 * The "Log" tab shared by the build and publication profile dialogs: Download/Find/Settings
 * action buttons plus the Log/Grid sub-tabs (mc-live-log / mc-log-stream-grid). The owning
 * dialog resolves the log-preparation polling and the entity-specific download action; this
 * component only renders the shared shell around the two log surfaces.
 */
@Component({
  selector: 'mc-log-tab-panel',
  templateUrl: './log-tab-panel.component.html',
  styleUrls: ['./log-tab-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogTabPanelComponent {
  @Input() streamUrl: string;
  @Input() sourceLinkResolver?: LiveLogSourceLinkResolver;
  @Input() emptyLabelOverride?: string | null;
  @Input() loading = false;
  @Input() generalError?: string;
  @Input() detailedErrors?: string[];
  @Input() canDownloadLog = false;
  @Input() logUnavailable = false;
  @Input() gridId: string;
  @Input() settingsKey: string;
  @Input() emptySubject = 'this stream';

  /** The owning dialog handles the actual download (the log URL shape differs per entity). */
  @Output() downloadLog = new EventEmitter<void>();

  // Captured via a setter ViewChild because `<mc-live-log>` lives inside an ng-template
  liveLog: LiveLogComponent | null = null;

  @ViewChild('liveLog')
  set liveLogChild(value: LiveLogComponent | undefined) {
    this.liveLog = value ?? null;
    // OnPush: the toolbar buttons key off `liveLog`, so they need a re-render when the live-log
    // mounts (Log tab selected) or unmounts (different sub-tab selected).
    this.changeDetectorRef.markForCheck();

    // *matTabContent attaches/destroys mc-live-log on every switch to/away from the Log tab
    // (preserveContent defaults to false), so this setter alone - fired whenever the Log tab
    // becomes active, whether on initial mount or switching back - is enough to focus the
    // terminal so keydown.control.f works without clicking in first. Deferred a tick so the
    // child's own ViewChild (the CDK viewport) has resolved before we try to focus it.
    if (this.liveLog) {
      setTimeout(() => this.liveLog?.focus());
    }
  }

  constructor(
    private dialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  // Re-measures the live-log's virtual scroll viewport. Call after this component's container
  // is resized programmatically (e.g. the dialog's maximize button) — the CDK viewport only
  // auto-re-measures on a browser window resize, not a programmatic container resize.
  checkViewportSize() {
    this.liveLog?.checkViewportSize();
  }

  openFind() {
    this.liveLog?.openFind();
  }

  /** Open the live log's Settings dialog. The dialog applies each field change instantly via
   *  the `apply` callback below — no Save/Cancel, the user just dismisses the dialog when done. */
  openLogSettingsDialog() {
    const liveLog = this.liveLog;
    if (!liveLog) return;

    this.dialog.open<LiveLogSettingsDialogComponent, LiveLogSettingsDialogData, void>(LiveLogSettingsDialogComponent, {
      ...LiveLogSettingsDialogComponent.DialogConfig,
      data: {
        showIcons: liveLog.showIcons,
        timestampMode: liveLog.timestampMode,
        theme: liveLog.theme,
        apply: (changes: Partial<LiveLogSettings>) => {
          if (changes.showIcons !== undefined) { liveLog.setShowIcons(changes.showIcons); }
          if (changes.timestampMode !== undefined) { liveLog.setTimestampMode(changes.timestampMode); }
          if (changes.theme !== undefined) { liveLog.setTheme(changes.theme); }
        },
      }
    });
  }
}
